import uniqBy from 'lodash/uniqBy'
import promiseDag from 'promise-dag'
import { isAlive, isStateTreeNode } from '@invisr/mobx-state-tree'
import { refCache } from './record/record-cache'
import { flow } from 'mobx'

if (typeof window !== 'undefined') (window as any).refCache = refCache
class DataSourceFactory {
  dataSources = []
  pendingPromise = null
  pending: { all?: Promise<unknown>[] } & { [k: string]: Promise<unknown>} = {}
  history = null
  app = null

  constructor (app, context?: any) {
    this.app = app
    if (context && context.history) this.history = context.history
  }
  
  lookupRecords1 = async function (this: DataSourceFactory, text, rd) {
    const ds = this.getInstance(rd.dataSourceId)
    if (typeof ds.lookup === 'function') {
      const results = await ds.lookup(text, rd)
      for (const r of results || []) {
        refCache.addIfNew(ds.config.id, rd.value, r[rd.value], r)
      }
      return results.map(r => ({ label: r[rd.label], value: r[rd.value] }))
    }
  }  

  lookupRecords = async function (this: DataSourceFactory, text, _refData) {
    const refData = Array.prototype.concat(_refData)
    const results = await Promise.all(refData.map(rd => this.lookupRecords1(text, rd)))
    return results.flat()
  }

  async getRecordByKey (key, _refData) {
    const refData = Array.prototype.concat(_refData)
    for (const rd of refData) {
      const ds = this.getInstance(rd.dataSourceId)
      const cached = refCache.get(ds.config.id, rd.value, key)
      if (cached) return cached
      if (typeof ds.getRecordByKey === 'function') {
        const results = await ds.getRecordByKey(rd.value, key, rd)
        if (results && results.length) {
          refCache.addIfNew(ds.config.id, rd.value, key, results[0])
          return results[0]
        }
      }
    }
  }

  setFieldSets = (componentFieldSets = {}, preserveFields) => {
    if (componentFieldSets) {
      const dsFieldSets = {}
      Object.keys(componentFieldSets).forEach(dsId => {
        this.getInstance(dsId)
        const ds = this.app?.dataSources.get(dsId)
        ds &&
          ds.dataTransformer &&
          Object.keys(ds.dataTransformer.where.dataSourceFieldSets).forEach(d => {
            this.getInstance(d)
            dsFieldSets[d] = ds.dataTransformer.where.dataSourceFieldSets[d]
          })
        this.getInstance(dsId)
      })
      this.dataSources.forEach(d => {
        if (isStateTreeNode(d)) {
          if (!isAlive(d)) return
        }
        const k = d.id || d.config.id
        const instance = this.getInstance(k)
        instance.fieldSet = uniqBy(
          [
            ...(componentFieldSets[k] || []),
            ...(dsFieldSets[k] || []),
            ...(preserveFields === true ? instance.fieldSet || [] : [])
          ],
          'fieldPath'
        ).filter(f => f)
      })
    }
  }

  getDepsRecursively = (instance, context, obj = {}, dataReload) => {
    if (!instance || !instance.config) return obj
    const cfg = instance.config
    const deps = cfg.dataSourceDependencies().filter(depId => this.getInstance(depId))
    const id = cfg.id
    const runFn = flow(function * () {
      yield instance.config?.mapping?.mapper?.whenReady()
      if (!instance.dataFetched || dataReload) yield instance.query?.({ ...context || {} }, false)
    })

    obj[id] = [...deps, this.pending[id] ? () => this.pending[id] : runFn]

    if (instance.setIsLoading) instance.setIsLoading()

    for (const depId of deps) {
      const inst = this.getInstance(depId)
      if (inst.setIsLoading) inst.setIsLoading()
      if (!obj[depId]) {
        this.getDepsRecursively(inst, context, obj, dataReload)
      } else {
        if (obj[depId].includes(cfg.id)) {
          // remove cyclic dependency
          obj[depId].splice(obj[depId].indexOf(cfg.id), 1)
        }
      }
    }

    return obj
  }

  loadData = (dsFieldSets = {}, context, preserveFields = false, dataReload = false) => {
    this.setFieldSets(dsFieldSets, preserveFields)
    try {
      return this.reloadData(Object.keys(dsFieldSets).map(this.getInstance), context, dataReload)
    } catch (e) {
      console.log(e)
    }
  }

  reloadData = (dataSources, context, dataReload) => {
    if (!dataSources) dataSources = this.dataSources
    const promises = {}
    dataSources.forEach(instance => {
      this.getDepsRecursively(instance, context, promises, dataReload)
    })
    const keys = Object.keys(promises)
    if (keys.length) {
      Object.assign(this.pending, promiseDag.run({
        all: [...keys, () => {}],
        ...promises
      }))
      for(let k of Object.keys(this.pending)) {
        let f = () => delete this.pending[k]
        this.pending[k].then(f, f)
      }
      return this.pending.all
    }
  }

  getInstance = dsId => {
    if (!dsId) return null
    let ds = this.dataSources.find(d => {
      if (isStateTreeNode(d)) {
        if (!isAlive(d)) return false
      }
      return d.id === dsId || (d.config && d.config.id === dsId)
    })
    if (ds) return ds
    const dsConfig = this.app?.dataSources.get(dsId)
    if (dsConfig && dsConfig.provider) {
      ds = new dsConfig.provider(dsConfig, dsConfig.type === 'Client Routes' && { history: this.history })
      this.dataSources.push(ds)
    }
    return ds
  }

  deleteInstance = dsId => {
    if (!dsId) return null
    this.dataSources = this.dataSources.filter(d => {
      if (d.id === dsId || (d.config && d.config.id === dsId)) {
        return false
      }
      return true
    })
  }
}

export default DataSourceFactory
